import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, ApiError } from '../lib/api';
import { errorText } from '../i18n';
import { toast } from '@vatman/ui';

/** A page of the documents (crates/server/src/docs, `PageRow::dto`). */
export interface Page {
  id: string;
  spaceId: string;
  spaceName: string;
  teamId: string;
  orgId: string | null;
  parentId: string | null;
  position: string;
  title: string;
  icon: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  /** Live pages right under it. */
  children: number;
  /** Closed (itself or a page above it): not everyone in the space reads or edits it. */
  closed: boolean;
  role: 'admin' | 'editor' | 'commenter' | 'viewer' | 'none';
  /** `GET /pages/{id}`: the pages above it, from the top. */
  ancestors?: { id: string; title: string; icon: string | null }[];
  labels?: string[];
  /** `post`: of the space's news (docs/News.tsx), outside the tree; published then, a draft without. */
  kind?: 'page' | 'post';
  publishedAt?: number | null;
  cover?: string | null;
  /** Approval (docs/Review.tsx): a round open, changes asked for, approved, edited since. */
  approval?: 'review' | 'changes' | 'approved' | 'changed' | null;
  /** Imported: from where, and its address there (Confluence). */
  origin?: { kind: 'confluence' | 'markdown'; url?: string } | null;
}

export interface TrashedPage {
  id: string;
  title: string;
  icon: string | null;
  deletedAt: number;
  deletedBy: string | null;
  pagesUnder: number;
}

export const docKeys = {
  all: ['pages'] as const,
  level: (space: string, parent: string | null) => ['pages', 'level', space, parent] as const,
  page: (id: string) => ['pages', 'page', id] as const,
  trash: (space: string) => ['pages', 'trash', space] as const,
};

/** One level of a space's tree (its top pages, or a page's children). */
export function usePageLevel(space: string | null, parent: string | null, enabled = true) {
  return useQuery({
    queryKey: docKeys.level(space ?? '', parent),
    queryFn: () => api.get<Page[]>(`/pages?space=${space}${parent ? `&parent=${encodeURIComponent(parent)}` : ''}`),
    enabled: !!space && enabled,
    staleTime: 30_000,
  });
}

export function usePage(id: string | null) {
  return useQuery({ queryKey: docKeys.page(id ?? ''), queryFn: () => api.get<Page>(`/pages/${id}`), enabled: !!id });
}

export const canEditPage = (p: Pick<Page, 'role'> | undefined) => p?.role === 'admin' || p?.role === 'editor';

/** The page's address. */
export const pagePath = (id: string) => `/d/${id}`;

/** A title for lists: the page's own, else "Untitled". */
export const pageTitle = (p: { title: string }, untitled: string) => p.title.trim() || untitled;

export function refreshPages(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: docKeys.all });
  void qc.invalidateQueries({ queryKey: ['spaces'] });
}

export async function createPage(body: { spaceId?: string | null; teamId?: string | null; parentId?: string | null; afterId?: string | null; title?: string; markdown?: string }): Promise<Page> {
  return api.post<Page>('/pages', body);
}

export async function movePage(id: string, to: { spaceId?: string; parentId: string | null; beforeId?: string | null; afterId?: string | null }): Promise<Page> {
  return api.post<Page>(`/pages/${id}/move`, to);
}

/** Opens a new page: at the top of a space, in a team's default space, or under a page. */
export function useNewPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (where: { spaceId?: string | null; teamId?: string | null; parentId?: string | null }) => {
    try {
      const page = await createPage(where);
      refreshPages(qc);
      navigate(pagePath(page.id));
    } catch (e) {
      toast(errorText(e instanceof ApiError ? e.code : 'internal'));
    }
  };
}
